import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Loader2, MessageCircle, RefreshCw, Copy, Check,
  Mail, User, Building2, Package as PackageIcon,
  Ruler, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;

// XHR helper (fetch is monkey-patched on this platform).
function xhrJson(method, url, body, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 20000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = {}; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: {} });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: {} });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

const STATUSES = [
  { id: 'all',       label: 'All' },
  { id: 'open',      label: 'Open' },
  { id: 'converted', label: 'Converted' },
  { id: 'closed',    label: 'Closed' },
];

const INTENT_LABELS = {
  pricing: 'Pricing',
  credits: 'Credits',
  examples: 'Examples',
  general: 'General',
  buy: 'Purchase',
  agency: 'Agency',
  lead: 'Lead',
  info: 'Info',
  other: 'Other',
};

const URGENCY_COLOR = {
  high: 'bg-rose-100 text-rose-700 border-rose-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-500 border-gray-200',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
};

const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Display name fallback: lead_name → lead_email → email → "Visitante"
const displayName = (c) => c?.lead_name || c?.lead_email || c?.email || 'Visitante';

// Surface metadata from the latest assistant message (already structured per
// Phase 7 — intent/package/size/agency_interest/urgency).
const summarizeMeta = (messages) => {
  const last = [...(messages || [])].reverse().find(m => m.sender_type === 'assistant' && m.metadata);
  return last?.metadata || {};
};

export default function SalesView() {
  const { session } = useAuth();
  const token = session?.access_token;

  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [copyState, setCopyState] = useState({ key: null });

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const lastTokenRef = useRef(token);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const fetchConversations = useCallback(async ({ silent = false } = {}) => {
    if (!token) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true);
    else setLoading(true);
    const qs = filterRef.current !== 'all' ? `?status=${filterRef.current}` : '';
    const cb = `${qs ? '&' : '?'}_=${Date.now()}`;
    const { ok, data } = await xhrJson(
      'GET',
      `${API}/api/sales/conversations${qs}${cb}`,
      null,
      headers,
    );
    setItems(ok ? (data.conversations || []) : []);
    setLoading(false);
    setRefreshing(false);
  }, [token, headers]);

  // Token changes (login/logout/role switch) → wipe local state, refetch fresh.
  useEffect(() => {
    if (lastTokenRef.current !== token) {
      lastTokenRef.current = token;
      setItems([]);
      setActiveId(null);
      setActiveDetail(null);
      setActiveMessages([]);
    }
    fetchConversations();
  }, [token, fetchConversations]);

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Auto-refresh every 15s + when tab becomes visible.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => fetchConversations({ silent: true }), POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchConversations({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, fetchConversations]);

  const markRead = useCallback(async (id) => {
    if (!id || !token) return;
    // Track previous unread state so we only emit the decrement event once
    // when actually clearing an unread conversation.
    const wasUnread = items.some(c => c.id === id && (c.is_read === false || (c.unread_count || 0) > 0));
    const { ok, data } = await xhrJson(
      'PATCH',
      `${API}/api/sales/conversations/${id}/read`,
      null,
      headers,
    );
    if (ok && data?.conversation) {
      setItems(prev => prev.map(c => c.id === id
        ? { ...c, is_read: true, unread_count: 0, last_read_at: data.conversation.last_read_at }
        : c));
      if (wasUnread) {
        // Notify the sidebar badge hook to decrement immediately
        try { window.dispatchEvent(new CustomEvent('ruido:sales-conv-read', { detail: { id } })); } catch { /* noop */ }
      }
    }
  }, [token, headers, items]);

  const openConversation = useCallback(async (conv) => {
    // Optimistic state update so the right pane renders immediately
    setActiveId(conv.id);
    setActiveDetail(conv);
    setActiveMessages([]);
    setActiveLoading(true);
    // GET /conversations/{id} already returns messages (oldest-first via the
    // /messages endpoint shape) — single round-trip.
    const cb = `?_=${Date.now()}`;
    const { ok, data } = await xhrJson(
      'GET',
      `${API}/api/sales/conversations/${conv.id}${cb}`,
      null,
      headers,
    );
    if (ok) {
      if (data?.conversation) setActiveDetail(data.conversation);
      setActiveMessages(data?.messages || []);
    }
    setActiveLoading(false);
    // Mark as read (optimistic clears the badge in the list immediately)
    markRead(conv.id);
  }, [headers, markRead]);

  const refresh = useCallback(async () => {
    await fetchConversations({ silent: true });
    if (activeId) {
      const cb = `?_=${Date.now()}`;
      const { ok, data } = await xhrJson(
        'GET',
        `${API}/api/sales/conversations/${activeId}${cb}`,
        null,
        headers,
      );
      if (ok) {
        if (data?.conversation) setActiveDetail(data.conversation);
        setActiveMessages(data?.messages || []);
      }
    }
  }, [fetchConversations, activeId, headers]);

  const copyToClipboard = useCallback(async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopyState({ key });
    setTimeout(() => setCopyState({ key: null }), 1400);
  }, []);

  const buildSummary = (conv, messages) => {
    const meta = summarizeMeta(messages);
    const lines = [
      `Ruido.ai conversation — ${formatRelative(conv?.last_message_at || conv?.created_at)}`,
      `Name: ${conv?.lead_name || '—'}`,
      `Email: ${conv?.lead_email || conv?.email || '—'}`,
      `Phone: ${meta?.lead_phone || '—'}`,
      `Intent: ${INTENT_LABELS[meta?.intent || conv?.intent] || meta?.intent || conv?.intent || '—'}`,
      `Suggested package: ${meta?.package_id || '—'}`,
      `Suggested size: ${meta?.video_size || '—'}`,
      `Urgency: ${meta?.urgency || '—'}`,
      `Agency: ${meta?.agency_interest ? 'yes' : 'no'}`,
      `Messages: ${conv?.message_count ?? messages?.length ?? 0}`,
      '',
      'Latest messages:',
    ];
    const tail = (messages || []).slice(-6);
    for (const m of tail) {
      const who = m.sender_type === 'assistant' ? 'AI'
                : (m.sender_type === 'user' || m.sender_type === 'visitor') ? 'Visitor'
                : m.sender_type;
      lines.push(`  · [${who}] ${(m.content || '').slice(0, 200)}`);
    }
    return lines.join('\n');
  };

  // Visible items respect filter (server already does, but extra guard)
  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(c => c.status === filter);
  }, [items, filter]);

  // Detail panel meta (last assistant message)
  const detailMeta = useMemo(() => summarizeMeta(activeMessages), [activeMessages]);

  return (
    <div className="flex h-full bg-[#f7f6f4]" data-testid="sales-view">
      {/* LEFT — Conversation list */}
      <div className="w-full md:w-[340px] lg:w-[380px] shrink-0 border-r border-gray-200/60 bg-white/40 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200/60 flex items-center gap-3">
          <h2 className="text-sm font-medium text-gray-900 font-['Outfit']">Ventas</h2>
          <button
            data-testid="sales-refresh"
            onClick={refresh}
            className="ml-auto p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-black/[0.04] transition-colors"
            aria-label="Actualizar"
            title="Actualizar"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="px-3 pt-2 pb-2 flex gap-1.5 flex-wrap border-b border-gray-100">
          {STATUSES.map(f => (
            <button
              key={f.id}
              data-testid={`sales-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
                filter === f.id
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto" data-testid="sales-list-scroll">
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin opacity-40" />
              <p className="text-sm">Loading…</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-gray-400 px-6">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No conversations yet.</p>
            </div>
          ) : (
            <div>
              {visible.map((c) => {
                const isActive = c.id === activeId;
                const unread = !c.is_read || (c.unread_count || 0) > 0;
                const last = c.last_message;
                const intent = c.intent ? (INTENT_LABELS[c.intent] || c.intent) : null;
                return (
                  <div
                    key={c.id}
                    data-testid={`sales-item-${c.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openConversation(c)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openConversation(c);
                      }
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-3 border-b border-gray-100/70 cursor-pointer select-none ${
                      isActive ? 'bg-gray-100/80' : 'hover:bg-black/[0.02]'
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-xs font-medium pointer-events-none ${
                      c.lead_name || c.lead_email
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {(c.lead_name || c.lead_email || 'V').slice(0, 1).toUpperCase()}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0 pointer-events-none">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-sm truncate ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {displayName(c)}
                        </span>
                        <span className="ml-auto text-[10px] text-gray-400 shrink-0">
                          {formatRelative(c.last_message_at || c.created_at)}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${unread ? 'text-gray-700' : 'text-gray-400'}`}>
                        {last?.sender_type === 'assistant' ? '✦ ' : ''}
                        {last?.content || 'No messages'}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {intent && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] text-gray-500">
                            {intent}
                          </span>
                        )}
                        {c.status && c.status !== 'open' && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] ${
                            c.status === 'converted' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {c.status === 'converted' ? 'Converted' : 'Closed'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Unread red badge */}
                    {unread && (
                      <span
                        data-testid={`sales-unread-${c.id}`}
                        className="mt-2 shrink-0 min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-medium flex items-center justify-center pointer-events-none"
                      >
                        {c.unread_count > 0 ? c.unread_count : '•'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Active conversation */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#f0efed]">
        {!activeDetail ? (
          <div className="flex-1 flex items-center justify-center" data-testid="sales-empty-detail">
            <div className="text-center">
              <MessageCircle className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Select a conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200/60 bg-white/50 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium shrink-0">
                {(activeDetail.lead_name || activeDetail.lead_email || 'V').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 block truncate" data-testid="sales-detail-name">
                  {displayName(activeDetail)}
                </span>
                <span className="text-[11px] text-gray-400 truncate block">
                  {activeDetail.lead_email || activeDetail.email || activeDetail.visitor_token?.slice(0, 12) || 'Anonymous visitor'}
                  {' · '}
                  {activeDetail.message_count || activeMessages.length || 0} messages
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {(activeDetail.lead_email || activeDetail.email) && (
                  <button
                    data-testid="sales-copy-email"
                    onClick={() => copyToClipboard('email', activeDetail.lead_email || activeDetail.email)}
                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-white/70 border border-gray-200/60 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1.5"
                    title="Copy email"
                  >
                    {copyState.key === 'email' ? <Check className="w-3 h-3 text-emerald-600" /> : <Mail className="w-3 h-3" />}
                    {copyState.key === 'email' ? 'Copied' : 'Copy email'}
                  </button>
                )}
                <button
                  data-testid="sales-copy-summary"
                  onClick={() => copyToClipboard('summary', buildSummary(activeDetail, activeMessages))}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors flex items-center gap-1.5"
                  title="Copy full summary"
                >
                  {copyState.key === 'summary' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copyState.key === 'summary' ? 'Copied' : 'Copy summary'}
                </button>
              </div>
            </div>

            {/* Metadata strip */}
            {(detailMeta.intent || detailMeta.package_id || detailMeta.video_size || detailMeta.urgency || detailMeta.agency_interest) && (
              <div data-testid="sales-detail-meta" className="px-5 py-2 border-b border-gray-200/60 bg-white/30 flex items-center gap-2 flex-wrap">
                {detailMeta.agency_interest && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px]">
                    <Building2 className="w-3 h-3" /> Agency
                  </span>
                )}
                {detailMeta.package_id && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px]">
                    <PackageIcon className="w-3 h-3" /> {detailMeta.package_id}
                  </span>
                )}
                {detailMeta.video_size && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 border border-sky-200 text-sky-700 text-[11px]">
                    <Ruler className="w-3 h-3" /> {detailMeta.video_size}
                  </span>
                )}
                {detailMeta.urgency && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] ${URGENCY_COLOR[detailMeta.urgency] || URGENCY_COLOR.low}`}>
                    <Zap className="w-3 h-3" /> {detailMeta.urgency}
                  </span>
                )}
                {detailMeta.intent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[11px]">
                    {INTENT_LABELS[detailMeta.intent] || detailMeta.intent}
                  </span>
                )}
                {(activeDetail.lead_name || detailMeta.lead_phone) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-200 text-gray-600 text-[11px]">
                    <User className="w-3 h-3" />
                    {activeDetail.lead_name || ''}{detailMeta.lead_phone ? ` · ${detailMeta.lead_phone}` : ''}
                  </span>
                )}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-2.5" data-testid="sales-messages">
                {activeLoading && activeMessages.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin opacity-40" />
                  </div>
                ) : activeMessages.length === 0 ? (
                  <div className="text-center py-16 text-gray-400 text-sm">No messages in this conversation.</div>
                ) : (
                  activeMessages.map((m) => {
                    const isVisitor = m.sender_type === 'user' || m.sender_type === 'visitor';
                    const isSystem = m.sender_type === 'system';
                    return (
                      <div
                        key={m.id}
                        data-testid={`sales-msg-${m.sender_type}`}
                        className={`flex ${isVisitor ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                          isVisitor
                            ? 'bg-emerald-500 text-white'
                            : isSystem
                              ? 'bg-amber-50 border border-amber-200 text-amber-800'
                              : 'bg-white border border-gray-200/60 text-gray-700 shadow-sm'
                        }`}>
                          <p className="whitespace-pre-wrap">{m.content}</p>
                          <span className={`block mt-1 text-[10px] ${isVisitor ? 'text-white/70' : 'text-gray-400'}`}>
                            {formatTime(m.created_at)}
                            {m.message_type && m.message_type !== 'text' ? ` · ${m.message_type}` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
